import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Yii } from "libraries/interfaces/Yii";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ISimpleSelectFieldConfig } from "libraries/module/formbuilder/fields/simple-select/simple-select.interface";
import { ConfigurationResourceService } from "libraries/service/resources/configuration/configurationResource.service";
import { LocalizedPipe } from "libraries/pipes/localized/localized.pipe";
import {
    DEFAULT_CONFIG_OPTIMISATION_PARAMS
} from "libraries/component/booking-information/components/booking-fare-optimisation-card/components/fare-optimisation-reject-form/default-config-optimisation-params";
import {
    IFareAssistant
} from "libraries/component/booking-information/components/booking-fare-optimisation-card/components/fare-optimisation-reject-form/i-fare-assistant";
import {
    FareScope
} from "libraries/component/booking-information/components/booking-fare-optimisation-card/components/fare-optimisation-reject-form/fare-scope";
import { IRejectFareOptimisationDataModal, IRejectFareOptimisationRequestParams } from "libraries/service/booking/enums/fare-optimisation";
import { IDateFieldSpecs, IFieldSpecs } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import { RequiredIf } from "libraries/module/formbuilder/forms/utils/validators/requiredIf.validator";
import { MaxChars } from "libraries/module/formbuilder/forms/utils/validators/max-chars.validator";
import { MinChars } from "libraries/module/formbuilder/forms/utils/validators/min-chars.validator";
import { NotIncludeChar } from "libraries/module/formbuilder/forms/utils/validators/not-include-char.validator";
import { INumberFieldSpecs } from 'libraries/module/formbuilder/fields/number/INumberFieldSpecs';
import { FareOptimisationRequesterService } from "libraries/service/resources/fare-optimisation/fare-optimisation-requester.service";

class Wording {
    public readonly onlyThisTime: string = Yii.t('wui-sasha-js', 'Juste cette fois');
    public readonly definitive: string = Yii.t('wui-sasha-js', 'Définitif');
    public readonly timeLimited: string = Yii.t('wui-sasha-js', 'Limité dans le temps');
    public readonly entireBooking: string = Yii.t('wui-sasha-js', 'Tout le dossier');
    public readonly cancel: string = Yii.t('wui-sasha-js', 'Abandonner');
    public readonly confirm: string = Yii.t('wui-sasha-js', 'Confirmer');
    public readonly title: string = Yii.t('wui-sasha-js', "Rejet d'une optimisation");
    public readonly fixedDate: string = Yii.t('wui-sasha-js', "Date fixe");
    public readonly numberOfDays: string = Yii.t('wui-sasha-js', "Nombre de jours");
    public readonly startDateType: string = Yii.t('wui-sasha-js', "Date de départ");
    public readonly issueDate: string = Yii.t('wui-sasha-js', "Date d'émission");
    public readonly reasonRejection: string = Yii.t('wui-sasha-js', "Raison du rejet");
    public readonly scopeRefusal: string = Yii.t('wui-sasha-js', "Portée du refus");
    public readonly rejectionMotif: string = Yii.t('wui-sasha-js', "Motif du rejet");
    public readonly select: string = Yii.t('wui-sasha-js', "Séléctionner");
    public readonly timeLimitedUntilDate: string = Yii.t('wui-sasha-js', "Limitation dans le temps : jusqu'à");
    public readonly specifyReason: string = Yii.t('wui-sasha-js', "Précisez le motif");
    public readonly comparedTo: string = Yii.t('wui-sasha-js', "Avant :");
}

enum EFields {
    scopeRefusalSelect = 'scopeRefusalSelect',
    reasonRejectionSelect = 'reasonRejectionSelect',
    rejectionMotifText = 'rejectionMotifText',
    timeLimitedUntilDateText = 'timeLimitedUntilDateText',
    timeLimitedDaysText = 'timeLimitedDaysText',
    countingFrom = 'countingFrom',
}

enum EValueRadioButton {
    BeforeDeparture = 'BeforeDeparture',
    BeforeTicketing = 'BeforeTicketing',
}

@Component({
    selector: 'fare-optimisation-reject-form',
    template: `
        <card class="card" mat-dialog-content>
            <div class="card-header-content">
                <h1>{{ wording.title }}</h1>
                <div class="btn-container">
                    <btn type="actionLink"
                         [label]="wording.cancel"
                         (onClick)="close()"></btn>
                    <btn type="actionDo"
                         [label]="wording.confirm"
                         (onClick)="rejectFareOptimisation()"></btn>
                </div>
            </div>

            <div class="card-body-content">
                <div class="form-group" *ngIf="reasonRejectionConfig && scopeRefusalConfig">
                    <div class="first">
                        <combobox-field class="reason"
                                        [items]="reasonRejectionConfig.options"
                                        [fieldConfig]="reasonRejectionConfig">
                        </combobox-field>
                    </div>
                    <div>
                        <combobox-field class="scope"
                                        [items]="scopeRefusalConfig.options"
                                        [fieldConfig]="scopeRefusalConfig">
                        </combobox-field>
                    </div>
                </div>

                <div class="form-group">
                    <div class="rejection first" [class.isInactive]="!showRejectionMotifField">
                        <text [fieldConfig]="rejectionMotifFieldConfig" *ngIf="rejectionMotifFieldConfig && showRejectionMotifField"></text>
                    </div>

                    <div [class.isInactive]="!showTimeLimitedField">
                        <div>
                            <label>{{ wording.timeLimitedUntilDate }}</label>
                        </div>
                        <div *ngIf="showTimeLimitedField">
                            <mat-button-toggle-group appearance="legacy" name="fontStyle" aria-label="Font Style">
                                <mat-button-toggle class="toggle" value="fixedDate" [checked]="true"
                                                   (click)="showFixedDateField = true; showNbDaysField = false">{{ wording.fixedDate }}</mat-button-toggle>
                                <mat-button-toggle class="toggle" value="issueDate"
                                                   (click)="showFixedDateField = false; showNbDaysField = true">{{ wording.numberOfDays }}</mat-button-toggle>
                            </mat-button-toggle-group>
                        </div>

                        <div class="date" *ngIf="showFixedDateField">
                            <date-field [fieldConfig]="fixedDateFieldConfig"
                                        class="timeLimited">
                            </date-field>
                        </div>

                        <div *ngIf="showNbDaysField">
                            <div class="number">
                                <number [showPlusAndMinus]="true"
                                        [align]="'center'"
                                        [fieldConfig]="nbDaysFieldConfig">
                                </number>
                            </div>


                            <div class="label-compared">
                                <label>{{wording.comparedTo}}</label>
                            </div>
                            <div class="radio-button" [formGroup]="rejectFormGroup">
                                <mat-radio-group [formControlName]="'countingFrom'">
                                    <mat-radio-button [checked]="true"
                                                      [value]="valueRadioButtonEnum.BeforeDeparture">{{ wording.startDateType }}</mat-radio-button>
                                    <mat-radio-button [value]="valueRadioButtonEnum.BeforeTicketing">{{ wording.issueDate }}</mat-radio-button>
                                </mat-radio-group>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </card>
    `,
    styles: [`
        .card {
            width: 40em;
            display: inline-block;
            flex-direction: column;
            justify-content: space-between;
            overflow: unset;
        }

        card-header-content {
            display: flex;
            justify-content: space-between;
        }

        .card-body-content {
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 3rem;
        }

        .form-group {
            display: flex;
            padding: 0.75rem;
        }

        .form-group > div {
            flex: 1;
        }

        .toggle {
            color: black;
        }

        .first {
            margin-right: .75rem;
        }

        .date, .number {
            margin-top: 1rem;
        }

        .label-compared, .radio-button {
            margin-top: .5rem;
        }

        .isInactive {
            opacity: 0;
        }

        ::ng-deep .custom-dialog-container .mat-dialog-container {
            overflow: visible;
        }
    `]
})
export class FareOptimisationRejectFormComponent implements OnInit {

    public readonly today: Date = new Date();
    public wording: Wording = new Wording();
    public form: FormGroup;
    public showFixedDateField: boolean = false;
    public showNbDaysField: boolean = false;
    public scopeRefusalConfig: ISimpleSelectFieldConfig;
    public reasonRejectionConfig: ISimpleSelectFieldConfig;
    public showRejectionMotifField: boolean = false;
    public showTimeLimitedField: boolean = false;
    public fareScopeEnum: typeof FareScope = FareScope;
    public valueRadioButtonEnum: typeof EValueRadioButton = EValueRadioButton;
    public otherReason: string;
    public nbDays: number;
    public fixedDate: string;
    public countingFrom: string;

    public rejectionMotifFieldConfig: IFieldSpecs;
    public fixedDateFieldConfig: IDateFieldSpecs;
    public nbDaysFieldConfig: INumberFieldSpecs;

    public rejectFormGroup: FormGroup = new FormGroup({
        scopeRefusalSelect: new FormControl(''),
        reasonRejectionSelect: new FormControl(''),
        rejectionMotifText: new FormControl(''),
        timeLimitedUntilDateText: new FormControl(''),
        timeLimitedDaysText: new FormControl(''),
        countingFrom: new FormControl(''),
    });

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: IRejectFareOptimisationDataModal,
        private readonly matDialogRef: MatDialogRef<FareOptimisationRejectFormComponent>,
        private readonly configurationResourceService: ConfigurationResourceService,
        private readonly localizedPipe: LocalizedPipe,
        private readonly fareOptimisationRequesterService: FareOptimisationRequesterService,
    ) {
    }

    public ngOnInit(): void {
        this.configurationResourceService.get(['wui', 'app_sasha', 'config', 'optimisation-params', 'optimisation-params.json'], DEFAULT_CONFIG_OPTIMISATION_PARAMS)
            .subscribe((config: IFareAssistant[]): void => {
                this.reasonRejectionConfig = {
                    formGroup: this.rejectFormGroup,
                    description: {
                        main: Yii.t('wui-sasha-js', "Raison du rejet de l'optimisation"),
                        detailList: config.map((item: IFareAssistant): string => {
                            return `${this.localizedPipe.transform(item.labelArray)} : ${this.localizedPipe.transform(item.descriptionArray)}`;
                        })
                    },
                    formControlName: EFields.reasonRejectionSelect,
                    placeholder: Yii.t('wui-sasha-js', 'Sélectionner'),
                    label: this.wording.reasonRejection,
                    options: config.map((item: IFareAssistant): { label: string, value: string } => {
                        return {
                            value: item.code,
                            label: this.localizedPipe.transform(item.labelArray)
                        };

                    }),
                    validators: [
                        {
                            validator: Validators.required,
                            errors: {
                                required: {
                                    when: "touched",
                                    message: Yii.t('wui-shared-js', 'Veuillez indiquer la raison du rejet'),
                                }
                            }
                        }
                    ]
                };
            });

        this.scopeRefusalConfig = {
            formGroup: this.rejectFormGroup,
            description: {
                main: Yii.t('wui-sasha-js', "Portée de refus de l'optimisation"),
                detailList: [
                    Yii.t('wui-sasha-js', "Juste cette fois = rejeter cette proposition uniquement cette fois-ci"),
                    Yii.t('wui-sasha-js', "Définitif = rejeter cette proposition d’une façon définitive"),
                    Yii.t('wui-sasha-js', "Limité dans le temps = rejeter cette proposition uniquement pour une période limitée"),
                    Yii.t('wui-sasha-js', "Tout le dossier = rejeter toutes les propositions d’optimisation sur ce dossier pour une période limitée")
                ]
            },
            formControlName: EFields.scopeRefusalSelect,
            placeholder: Yii.t('wui-sasha-js', 'Sélectionner'),
            label: this.wording.scopeRefusal,
            options: [
                {value: this.fareScopeEnum.OnlyThisTime, label: this.wording.onlyThisTime},
                {value: this.fareScopeEnum.Definitive, label: this.wording.definitive},
                {value: this.fareScopeEnum.TimeLimited, label: this.wording.timeLimited},
                {value: this.fareScopeEnum.EntireBooking, label: this.wording.entireBooking},
            ],
            validators: [
                {
                    validator: Validators.required,
                    errors: {
                        required: {
                            when: "touched",
                            message: Yii.t('wui-shared-js', 'Veuillez indiquer la portée du refus'),
                        }
                    }
                }
            ]
        };

        this.nbDaysFieldConfig = {
            formGroup: this.rejectFormGroup,
            formControlName: EFields.timeLimitedDaysText,
            placeholder: this.wording.numberOfDays,
            min: 1,
            fixed: 0,
            validators: [
                new RequiredIf(this.isValidationNbDays.bind(this)),
            ]
        };

        this.fixedDateFieldConfig = {
            formGroup: this.rejectFormGroup,
            formControlName: EFields.timeLimitedUntilDateText,
            minDate: this.today,
            placeholder: this.wording.fixedDate,
            validators: [
                new RequiredIf(this.isValidationDateFixed.bind(this)),
            ]
        };

        this.rejectionMotifFieldConfig = {
            formGroup: this.rejectFormGroup,
            formControlName: EFields.rejectionMotifText,
            placeholder: this.wording.specifyReason,
            label: Yii.t('wui-shared-js', this.wording.rejectionMotif),
            validators: [
                new RequiredIf(this.isValidationOther.bind(this)),
                new MinChars(this.rejectFormGroup.get(EFields.rejectionMotifText), 5, this.isValidationOther.bind(this)),
                new MaxChars(this.rejectFormGroup.get(EFields.rejectionMotifText), 140),
                new NotIncludeChar(this.rejectFormGroup.get(EFields.rejectionMotifText), '&'),
            ]
        };

        this.rejectFormGroup.get(EFields.scopeRefusalSelect).valueChanges.subscribe((value: string): void => {
            this.showFixedDateField = value.includes(this.fareScopeEnum.TimeLimited) || value.includes(this.fareScopeEnum.EntireBooking);
            this.showTimeLimitedField = value.includes(this.fareScopeEnum.TimeLimited) || value.includes(this.fareScopeEnum.EntireBooking);
            if (!this.showTimeLimitedField) {
                this.rejectFormGroup.get(EFields.timeLimitedUntilDateText).updateValueAndValidity();
                this.rejectFormGroup.get(EFields.timeLimitedDaysText).updateValueAndValidity();
            }

        });


        this.rejectFormGroup.get(EFields.reasonRejectionSelect).valueChanges.subscribe((value: string): void => {
            this.showRejectionMotifField = value.includes("RQA_ROTHER");
            if (!this.showRejectionMotifField) {
                this.rejectFormGroup.get(EFields.rejectionMotifText).updateValueAndValidity();
            }
        });

    }

    public isValidationOther(): boolean {
        return this.showRejectionMotifField;
    }

    public isValidationDateFixed(): boolean {
        return this.showFixedDateField;
    }

    public isValidationNbDays(): boolean {
        return this.showNbDaysField && this.showTimeLimitedField;
    }

    public close(): void {
        this.matDialogRef.close();
    }

    public rejectFareOptimisation(): void {
        this.rejectFormGroup.markAllAsTouched();
        if (this.rejectFormGroup.valid) {

            const rejectionReasonCode: string = this.rejectFormGroup.get(EFields.reasonRejectionSelect).value;
            const rejectionType: string = this.rejectFormGroup.get(EFields.scopeRefusalSelect).value;

            if (this.rejectFormGroup.get(EFields.rejectionMotifText).value && this.showRejectionMotifField) {
                this.otherReason = this.rejectFormGroup.get(EFields.rejectionMotifText).value;
            }

            if (this.rejectFormGroup.get(EFields.timeLimitedUntilDateText).value) {
                const options: {} = {year: 'numeric', month: '2-digit', day: '2-digit'};
                this.fixedDate = this.rejectFormGroup.get(EFields.timeLimitedUntilDateText).value.toLocaleDateString('en-US', options);
            }

            if (this.rejectFormGroup.get(EFields.timeLimitedDaysText).value) {
                this.nbDays = this.rejectFormGroup.get(EFields.timeLimitedDaysText).value;
                this.countingFrom = EValueRadioButton.BeforeDeparture;
            }

            if (this.rejectFormGroup.get(EFields.countingFrom).value) {
                this.countingFrom = this.rejectFormGroup.get(EFields.countingFrom).value;
            }

            const request: IRejectFareOptimisationRequestParams = this.fareOptimisationRequesterService.createRejectFareOptimisationRequest(
                this.data.syscode,
                this.data.host,
                this.data.priceQuotationTypeReference,
                this.data.priceStoredFareTypeReference,
                rejectionReasonCode,
                rejectionType,
                this.otherReason,
                this.fixedDate,
                this.nbDays,
                this.countingFrom);
            this.matDialogRef.close(request);
        }
    }
}

